"use client";

import React, { useEffect, useState } from "react";

import { reportJsError } from "@/lib/js-error-beacon";

export interface LiveTimingCountdownProps {
  labelEn: string;
  labelHi: string;
  /**
   * The absolute instant this period ends, as an ISO-8601 string carrying its
   * offset (`2026-08-14T21:45:00+05:30`) or a `Z` UTC instant.
   *
   * This is deliberately the ONLY way to give this component a deadline. The
   * engine's `start_time`/`end_time` fields are local wall-clock `"HH:MM"`
   * strings in the response's `tz_offset` and may belong to the next day — they
   * are display strings, not instants (rule 25: the backend owns the conversion,
   * the frontend only displays). Reconstructing an instant here from an hour and
   * a minute is what broke `/tithi-today` (NaN) and `/choghadiya` (IST-only), so
   * that prop path no longer exists.
   */
  endsAt: string;
  isAuspicious?: boolean;
  locale?: "en" | "hi";
  periodName?: string;
  periodNameHi?: string;
}

function formatRemaining(diffMs: number, isHi: boolean): { str: string; finished: boolean } {
  if (diffMs <= 0) return { str: isHi ? "समाप्त" : "Ended", finished: true };

  const totalSec = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return { str: hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`, finished: false };
}

type Tone = "ended" | "auspicious" | "inauspicious";

function toneOf(isFinished: boolean, isAuspicious: boolean): Tone {
  if (isFinished) return "ended";
  return isAuspicious ? "auspicious" : "inauspicious";
}

const SHELL_CLASS: Record<Tone, string> = {
  ended: "bg-surface-warm text-text-secondary border-border-subtle",
  auspicious: "bg-success/10 text-success border-success/30",
  inauspicious: "bg-error/10 text-error border-error/30",
};

const DOT_CLASS: Record<Tone, string> = {
  ended: "bg-text-muted",
  auspicious: "bg-success",
  inauspicious: "bg-error",
};

const PING_CLASS: Record<Tone, string> = {
  ended: "bg-text-muted",
  auspicious: "bg-success/70",
  inauspicious: "bg-error/70",
};

export default function LiveTimingCountdown({
  labelEn,
  labelHi,
  endsAt,
  isAuspicious = true,
  locale = "en",
  periodName,
  periodNameHi,
}: LiveTimingCountdownProps) {
  const isHi = locale === "hi";
  const targetMs = new Date(endsAt).getTime();
  const hasInstant = Number.isFinite(targetMs);

  const [timeLeftStr, setTimeLeftStr] = useState<string>("");
  const [isFinished, setIsFinished] = useState<boolean>(false);

  useEffect(() => {
    // A deadline we cannot parse is a broken data contract, not a display state.
    // Report it once and render nothing rather than counting down to NaN.
    if (!hasInstant) {
      reportJsError({
        error_name: "LiveTimingCountdownContract",
        error_message: `endsAt is not a parseable instant: ${endsAt}`,
        component: "LiveTimingCountdown",
      });
      return;
    }

    function tick() {
      const res = formatRemaining(targetMs - Date.now(), isHi);
      setTimeLeftStr(res.str);
      setIsFinished(res.finished);
      return res.finished;
    }

    if (tick()) return;

    const timer = setInterval(() => {
      if (tick()) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [endsAt, targetMs, hasInstant, isHi]);

  if (!hasInstant) return null;

  const displayName = isHi ? periodNameHi || labelHi : periodName || labelEn;
  const tone = toneOf(isFinished, isAuspicious);

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs sm:text-sm font-medium transition-all ${SHELL_CLASS[tone]}`}
      aria-live="polite"
      aria-label={`${displayName}: ${timeLeftStr} ${isHi ? "शेष" : "remaining"}`}
    >
      <span className="relative flex h-2 w-2">
        {!isFinished && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${PING_CLASS[tone]}`}
          />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${DOT_CLASS[tone]}`} />
      </span>
      {/* Explicit spaces keep the name, the clock and the unit separate words
          in the text content. Without them the server text reads "Amrit:left"
          and the hydrated text "Amrit:12:12left", and the certification's
          server-versus-hydrated word projection rejects the page. */}
      <span className="font-semibold">{displayName}:</span>{" "}
      <span className="font-mono font-bold tracking-wider">{timeLeftStr}</span>{" "}
      {!isFinished && <span className="text-caption opacity-80">{isHi ? "शेष" : "left"}</span>}
    </div>
  );
}
